<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <div class="flex items-center space-x-4">
          <UserAvatar :size="'2xl'" />
          <div>
            <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Profile Settings</h1>
            <p class="text-gray-600 dark:text-gray-300">
              Manage your account settings and preferences
            </p>
          </div>
        </div>
      </div>

      <!-- Navigation Tabs -->
      <div class="border-b border-gray-200 dark:border-gray-600 mb-8">
        <nav class="-mb-px flex space-x-8">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="setActiveTab(tab.id)"
            :class="[
              'py-2 px-1 border-b-2 font-medium text-sm',
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500',
            ]"
          >
            <component :is="tab.icon" class="w-5 h-5 mr-2 inline" />
            {{ tab.name }}
          </button>
        </nav>
      </div>

      <!-- Tab Content -->
      <div class="space-y-8">
        <!-- Profile Tab -->
        <div v-if="activeTab === 'profile'">
          <ProfileForm />
        </div>

        <!-- Security Tab -->
        <div v-if="activeTab === 'security'">
          <PasswordChangeForm />
        </div>

        <!-- Preferences Tab -->
        <div v-if="activeTab === 'preferences'">
          <UserSettings />
        </div>

        <!-- Danger Zone Tab -->
        <div v-if="activeTab === 'danger'">
          <AccountDeletion
            @account-deleted="handleAccountDeleted"
            @export-data="handleExportData"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import {
    UserIcon,
    ShieldCheckIcon,
    Cog6ToothIcon,
    ExclamationTriangleIcon,
  } from '@heroicons/vue/24/outline';
  import {
    UserAvatar,
    ProfileForm,
    PasswordChangeForm,
    UserSettings,
    AccountDeletion,
  } from '@/features/users/components';
  import { useUserProfile } from '@/features/users/composables/useUserProfile';

  const { activeTab, setActiveTab } = useUserProfile();

  // Event handlers
  const handleAccountDeleted = () => {
    // Redirect to home page after account deletion
    window.location.href = '/';
  };

  const handleExportData = () => {
    // Handle data export request
    alert('Data export functionality will be implemented in a future update.');
  };

  // Tab configuration
  const tabs = computed(() => [
    {
      id: 'profile',
      name: 'Profile',
      icon: UserIcon,
    },
    {
      id: 'security',
      name: 'Security',
      icon: ShieldCheckIcon,
    },
    {
      id: 'preferences',
      name: 'Preferences',
      icon: Cog6ToothIcon,
    },
    {
      id: 'danger',
      name: 'Danger Zone',
      icon: ExclamationTriangleIcon,
    },
  ]);
</script>
